import { FacilityAnalogGateway } from '@reliance/types/models/FacilityAnalogGateway';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import api from '../common/api';
import {
  FacilityAnalogGatewayRow,
  useFacilityAnalogGatewaysList,
} from '../hooks/FacilityAnalogGateway';
import { useNotifyTabLoading } from '../hooks/useTabLoadingReporter';
import { useSaltMinions } from '../hooks/SaltMinion';
import AddEditModal from './AddEditModal';
import { List } from './List';
import { useAlerts } from '../alert/AlertContext';

type AnalogGatewaysTabProps = {
  facility: { id: number };
};

const AnalogGatewaysTab: React.FC<AnalogGatewaysTabProps> = ({ facility }) => {
  const [modalShow, setModalShow] = useState(false);
  const [editGateway, setEditGateway] = useState<FacilityAnalogGateway | null>(null);
  const [gatewayMutationBusy, setGatewayMutationBusy] = useState(false);
  const alerts = useAlerts();

  const {
    execute: refetchGateways,
    result: gatewayRows,
    loading: listLoading,
    error: listError,
  } = useFacilityAnalogGatewaysList(facility.id);

  const saltMinionFilters = useMemo(
    () => ({ facilityId: facility.id, page: 1, perPage: 500 }),
    [facility.id]
  );
  const saltMinionsResult = useSaltMinions(saltMinionFilters);

  const pattonConfigureMinionId = useMemo(() => {
    const rows = saltMinionsResult.result?.data ?? [];
    const row = [...rows].reverse().find(
      (m) => m.id != null && String(m.id).indexOf('relcore') !== -1
    );
    return row?.id ?? null;
  }, [saltMinionsResult.result?.data]);

  useNotifyTabLoading('analog-gateways', listLoading || gatewayMutationBusy);

  useEffect(() => {
    if (listError != null) {
      alerts.error('An error occurred while loading analog gateways');
    }
  }, [listError]);

  const openModal = useCallback((row?: FacilityAnalogGatewayRow | null) => {
    setEditGateway(row ?? null);
    setModalShow(true);
  }, []);

  const closeModal = useCallback(
    (saved: boolean) => {
      setModalShow(false);
      setEditGateway(null);
      if (saved) {
        void refetchGateways(facility.id);
      }
    },
    [facility.id, refetchGateways]
  );

  const onDelete = useCallback(
    (row: FacilityAnalogGatewayRow) => {
      if (row.id == null) {
        return;
      }
      if (!window.confirm('Are you sure to want to delete this gateway?')) {
        return;
      }
      setGatewayMutationBusy(true);
      void api.delete(`/facility-analog-gateway/${row.id}`).then(
        () => {
          alerts.success('The gateway was deleted');
          void refetchGateways(facility.id);
        },
        () => {
          alerts.error('An error occurred while deleting the gateway');
        }
      ).finally(() => setGatewayMutationBusy(false));
    },
    [facility.id, refetchGateways]
  );

  const onConfigure = useCallback(
    (row: FacilityAnalogGatewayRow) => {
      const minionId = pattonConfigureMinionId;
      if (minionId == null || minionId === '') {
        alerts.error('Unable to send the commands to the gateway');
        return;
      }

      setGatewayMutationBusy(true);
      void api
        .put(`/salt/minion/${minionId}/state/freeswitch.rt-switch.patton`, {
          keywordArgs: {
            refresh_pillar: true,
            sync_mods: 'modules',
            pillar: {
              patton_smartnode: {
                target: row.name,
              },
            },
          },
        })
        .then(
          () => {
            alerts.success('The gateway will be configured shortly');
          },
          () => {
            alerts.error('An unexpected error occurred when configuring the gateway');
          }
        )
        .finally(() => setGatewayMutationBusy(false));
    },
    [pattonConfigureMinionId]
  );

  return (
    <div className="jarviswidget jarviswidget-color-blueDark">
      <div className="content">
        <Row>
          <Col md={12}>
            <Button
              type="button"
              variant="primary"
              className="btn-labeled mb-2"
              onClick={() => openModal(null)}>
              <span className="btn-label">
                <i className="fas fa-plus-circle" />{' '}
              </span>
              Add Gateway
            </Button>
            <List
              rows={gatewayRows ?? []}
              loading={listLoading}
              onEdit={openModal}
              onDelete={onDelete}
              onConfigure={onConfigure}
            />
            <AddEditModal
              facility={facility}
              show={modalShow}
              editGateway={editGateway}
              onClose={closeModal}
            />
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default AnalogGatewaysTab;
